import { useCallback } from "react";
import { useQueryClient } from "react-query";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";
import { usePlanFeatures } from "@shared/queryHooks/useBilling";
import { getModalDisplayName } from "@shared/lib/planHelpers";

interface PublishJobGateResult {
  shouldProceed: boolean;
  modalType?: "upgrade" | "freeTrial" | "reactivate";
  modalData?: {
    currentPlanName?: string;
    jobLimit?: number;
    nextPlanName?: string;
    publishedJobsCount?: number;
    recommendedPlanName?: string;
  };
}

export function usePublishJobGate() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useCurrentSession();
  const { data: planFeaturesData, isLoading: isLoadingPlanFeatures } = usePlanFeatures({
    refetchOnWindowFocus: false,
  });

  const checkGate = useCallback((): PublishJobGateResult => {
    queryClient.invalidateQueries(["currentOrganization"]);

    window.logger("%c[usePublishJobGate] check gate", "color: #4CAF50", {
      isLoadingPlanFeatures,
      plansLength: planFeaturesData?.plans?.length,
      stripeSubscriptionInGoodStanding: currentOrganization?.stripeSubscriptionInGoodStanding,
      plan: currentOrganization?.plan,
      publishedJobsCount: currentOrganization?.publishedJobsCount,
    });

    if (!currentOrganization) {
      window.logger("%c[usePublishJobGate] Missing currentOrganization", "color: #f21919");
      return { shouldProceed: false };
    }

    if (isLoadingPlanFeatures) {
      window.logger(
        "%c[usePublishJobGate] Plan features still loading - button will show loading state",
        "color: #FF9800",
      );
      return { shouldProceed: false };
    }

    if (!planFeaturesData?.plans) {
      window.logger(
        "%c[usePublishJobGate] Missing plan features data after loading",
        "color: #f21919",
        { planFeaturesData },
      );
      return { shouldProceed: false };
    }

    const { stripeSubscriptionInGoodStanding, plan, publishedJobsCount } = currentOrganization;
    const plans = planFeaturesData.plans;

    // Case 1: No subscription at all — show free trial modal
    if (currentOrganization.stripeSubscriptionStatus === null) {
      return {
        shouldProceed: false,
        modalType: "freeTrial",
      };
    }

    // Case 2: Had subscription but now inactive — show restart modal
    if (!stripeSubscriptionInGoodStanding) {
      return {
        shouldProceed: false,
        modalType: "reactivate",
      };
    }

    // Case 3: Active subscription — check if at job limit
    const currentPlanData = plans.find((p) => p.plan === plan);
    if (!currentPlanData) {
      console.warn("Could not find current plan data");
      return { shouldProceed: true };
    }

    if (publishedJobsCount >= currentPlanData.jobLimit) {
      if (currentOrganization.eligibleForFreeTrial) {
        return {
          shouldProceed: false,
          modalType: "freeTrial",
        };
      }

      const nextPlan = plans
        .filter((p) => !p.legacyPlan && p.jobLimit > currentPlanData.jobLimit)
        .sort((a, b) => a.jobLimit - b.jobLimit)[0];

      return {
        shouldProceed: false,
        modalType: "upgrade",
        modalData: {
          currentPlanName: getModalDisplayName(currentPlanData.plan),
          jobLimit: currentPlanData.jobLimit,
          ...(nextPlan ? { nextPlanName: getModalDisplayName(nextPlan.plan) } : {}),
        },
      };
    }

    return { shouldProceed: true };
  }, [queryClient, currentOrganization, planFeaturesData, isLoadingPlanFeatures]);

  return {
    checkGate,
    isLoading: isLoadingPlanFeatures,
  };
}
